import { Injectable } from '@angular/core';
import { catchError, map, Observable, of, switchMap } from 'rxjs';
import { Permission, PermissionService, Role, UserRole } from '@openapi';
import { AuthenticationService } from '@auth/services/authentication.service';

@Injectable({
  providedIn: 'root',
})
export class PermissionClientService {
  private userRoles: UserRole[];
  private allPermissions: Permission[] = [];

  constructor(
    private permissionService: PermissionService,
    private authenticationService: AuthenticationService
  ) {}

  /**
   * Receives all permissions from the permission table
   *
   * @returns {Observable<Permission[]>} An observable of all Permissions.
   */
  public getAllPermissions(): Observable<Permission[]> {
    return this.permissionService.getAllPermissions();
  }

  /**
   * Receives all permissions from the permission table for a specific permission_type and filter all Permission for userRoles
   *
   * @param {string} permissionType - The ID of the structure for which properties are to be retrieved.
   * @returns {Observable<Permission[]>} An observable of permissions.
   */
  public getPermissions(permissionType: string): Observable<Permission[]> {
    return this.authenticationService.getUserRoles().pipe(
      switchMap(data => {
        if (data) {
          this.userRoles = data;

          const userRoleNames: string[] = this.userRoles.map(
            userRole => userRole.userRole
          );

          return this.permissionService.getAllPermissions().pipe(
            map(permissions => {
              this.allPermissions = permissions;

              return this.allPermissions.filter(
                permission =>
                  permission.enabled === true &&
                  permission.permission_type === permissionType &&
                  this.containsAny(permission.roles, userRoleNames)
              );
            })
          );
        }
      }),
      catchError(error => {
        console.error(error);
        return of([]);
      })
    );
  }

  /**
   * Receives all permissions from the permission table for a specific permission_type
   *
   * @param {string} permissionType
   * @returns {Observable<Permission[]>} An observable of permissions.
   */
  getPermissionForSpecificPermissionType(
    permissionType: string
  ): Observable<Permission[]> {
    return this.permissionService.getAllPermissions().pipe(
      map(permissions => {
        this.allPermissions = permissions;
        return this.allPermissions.filter(
          permission =>
            permission.enabled === true &&
            permission.permission_type === permissionType
        );
      })
    );
  }

  private containsAny(roles: Role[], targetRoles: string[]): boolean {
    return roles.some(role => targetRoles.includes(role.rolename));
  }
}
